import * as React from 'react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { useTranslation } from 'hooks/useTranslation';

type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

type OpenDialogParams = {
  title?: React.ReactNode;
  message?: React.ReactNode;
}

type Props = OpenDialogParams & {
  confirmationButtonProps?: ButtonProps;
  confirmationButtonVariant?: ButtonProps['variant'];
  confirmationButtonLabel?: React.ReactNode;
  cancelButtonProps?: ButtonProps;
  cancelButtonLabel?: React.ReactNode;
};

type ConfirmationResult = 'canceled' | 'confirmed';

export type ConfirmationDialogRef = {
  openDialog: (params?: OpenDialogParams) => Promise<ConfirmationResult>;
};

const translations = {
  message: {
    pt: 'Tem certeza que deseja continuar?',
    en: 'Are you sure you want to proceed?',
    es: '¿Estás seguro de que deseas continuar?',
    de: 'Sind Sie sicher, dass Sie fortfahren möchten?',
  },
  cancel: {
    pt: 'Cancelar',
    en: 'Cancel',
    es: 'Cancelar',
    de: 'Abbrechen',
  },
  confirm: {
    pt: 'Confirmar',
    en: 'Confirm',
    es: 'Confirmar',
    de: 'Bestätigen',
  },
};

export const ConfirmationDialog = React.forwardRef<
  ConfirmationDialogRef,
  Props
>((props, ref) => {
  const {
    title: defaultTitle = '',
    message: defaultMessage = 'Tem certeza de que deseja continuar?',
    confirmationButtonProps,
    confirmationButtonVariant = 'destructive',
    confirmationButtonLabel,
    cancelButtonProps,
    cancelButtonLabel,
  } = props;
  const [title, setTitle] = React.useState<React.ReactNode>(defaultTitle);
  const [message, setMessage] = React.useState<React.ReactNode>(defaultMessage);
  const [open, setOpen] = React.useState(false);
  const resolver = React.useRef((_value: ConfirmationResult) => {});
  const { getTranslation: t } = useTranslation(translations);

  const handleConfirm = React.useCallback(() => {
    resolver.current('confirmed');
    setOpen(false);
  }, []);

  const handleCancel = React.useCallback(() => {
    resolver.current('canceled');
    setOpen(false);
  }, []);

  const openDialog = React.useCallback((params?: OpenDialogParams): Promise<ConfirmationResult> => {
    if (params?.title) {
      setTitle(params.title);
    }

    if (params?.message) {
      setMessage(params.message);
    } else {
      setMessage(t('message'))
    }

    setOpen(true);

    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, [t]);

  React.useImperativeHandle(ref, () => {
    return { openDialog };
  }, [openDialog]);

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {message}
        <div className={'flex mt-6 gap-4'}>
          <Button
            type={'button'}
            size={'sm'}
            variant={'outline'}
            {...cancelButtonProps}
            onClick={handleCancel}
          >
            {cancelButtonLabel || t('cancel')}
          </Button>
          <Button
            type={'button'}
            size={'sm'}
            variant={confirmationButtonVariant}
            {...confirmationButtonProps}
            onClick={handleConfirm}
          >
            {confirmationButtonLabel || t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});
ConfirmationDialog.displayName = 'ConfirmationDialog';
